import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { cn } from '@/shared/lib';
import type {
  DropdownMenuContentProps,
  DropdownMenuGroupProps,
  DropdownMenuIndicatorProps,
  DropdownMenuLabelProps,
  DropdownMenuSeparatorProps,
} from '@/shared/ui/dropdown-menu/types';
import { dropdownMenuStyles } from '@/shared/ui/dropdown-menu/styles';

/**
 * The anchored menu surface: portal, positioner and popup.
 *
 * The popup carries `role="menu"` from the primitive, which is what tells a
 * screen reader that arrow keys navigate within it rather than leaving it.
 */
export function DropdownMenuContent({
  children,
  className,
  side,
  align,
  sideOffset,
}: DropdownMenuContentProps) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner side={side} align={align} sideOffset={sideOffset}>
        <MenuPrimitive.Popup className={cn(dropdownMenuStyles.popup, className)}>
          {children}
          <MenuPrimitive.Arrow className={dropdownMenuStyles.arrow} />
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

/** A non-interactive heading for a run of menu rows. */
export function DropdownMenuLabel({ children, className }: DropdownMenuLabelProps) {
  return (
    <MenuPrimitive.GroupLabel className={cn(dropdownMenuStyles.label, className)}>
      {children}
    </MenuPrimitive.GroupLabel>
  );
}

/** Groups related rows so arrow navigation skips over the label as a unit. */
export function DropdownMenuGroup({ children, className }: DropdownMenuGroupProps) {
  return <MenuPrimitive.Group className={cn(dropdownMenuStyles.group, className)}>{children}</MenuPrimitive.Group>;
}

/** A visual and semantic break between groups of rows. */
export function DropdownMenuSeparator({ className }: DropdownMenuSeparatorProps) {
  return (
    <MenuPrimitive.Separator className={cn(dropdownMenuStyles.separator, className)} />
  );
}

/** The tick or dot column inside a checkbox or radio row. */
export function DropdownMenuIndicator({ children, className }: DropdownMenuIndicatorProps) {
  return (
    <MenuPrimitive.CheckboxItemIndicator className={cn(dropdownMenuStyles.indicator, className)}>
      {children ?? '✓'}
    </MenuPrimitive.CheckboxItemIndicator>
  );
}

/** The tick column inside a radio row. */
export function DropdownMenuRadioIndicator({ children, className }: DropdownMenuIndicatorProps) {
  return (
    <MenuPrimitive.RadioItemIndicator className={cn(dropdownMenuStyles.indicator, className)}>
      {children ?? '•'}
    </MenuPrimitive.RadioItemIndicator>
  );
}